/**
 * Dial · Multi-circle tagging.
 *
 * Top-50 are stratified into circles: family, friend, work, mentor, school,
 * college, neighbour, occasional. A contact can sit in multiple. v0 derives
 * initial tags from the seed `relationship_kind` + `life_areas`, then lets
 * the user override locally (stored in OPFS).
 */
import { store } from '../store.ts';
import { Storage } from '../chassis/index.ts';
import { RELATIONSHIP_DISPLAY } from '../types.ts';
import type { Person, RelationshipKind } from '../types.ts';

type Circle = 'family' | 'friend' | 'work' | 'mentor' | 'school' | 'college' | 'neighbour' | 'occasional';
const CIRCLES: readonly Circle[] = ['family', 'friend', 'work', 'mentor', 'school', 'college', 'neighbour', 'occasional'];

const KIND_TO_CIRCLES: Record<RelationshipKind, readonly Circle[]> = {
  family: ['family'],
  partner: ['family', 'friend'],
  best_friend: ['friend'],
  close_friend: ['friend'],
  school: ['school', 'friend'],
  college: ['college', 'friend'],
  workplace: ['work'],
  neighbor: ['neighbour'],
  mentor: ['mentor', 'work'],
  acquaintance: ['occasional'],
  occasional: ['occasional'],
};

interface CircleOverrides {
  readonly [contactId: string]: readonly Circle[];
}

const FILE = 'dial-circle-overrides.json';

async function loadOverrides(): Promise<CircleOverrides> {
  return (await Storage.readFile<CircleOverrides>(FILE)) ?? {};
}
async function saveOverrides(o: CircleOverrides): Promise<void> {
  await Storage.writeFile(FILE, o);
}

function effectiveCircles(p: Person, overrides: CircleOverrides): readonly Circle[] {
  return overrides[p.id] ?? KIND_TO_CIRCLES[p.relationship_kind];
}

export class DialCircleTags extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #people: Person[] = [];
  #overrides: CircleOverrides = {};
  #filter: Circle | 'all' = 'all';

  async connectedCallback(): Promise<void> {
    this.#overrides = await loadOverrides();
    this.#unsub = store.subscribe((s) => {
      if (s.summary) {
        this.#people = s.summary.inner_circle.slice(0, 50);
        this.#render();
      }
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #setFilter(f: Circle | 'all'): void {
    this.#filter = f;
    this.#render();
  }

  async #toggle(personId: string, circle: Circle): Promise<void> {
    const person = this.#people.find((p) => p.id === personId);
    if (!person) return;
    const current = [...effectiveCircles(person, this.#overrides)];
    const idx = current.indexOf(circle);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(circle);
    const next: CircleOverrides = { ...this.#overrides, [personId]: current };
    this.#overrides = next;
    await saveOverrides(next);
    this.#render();
  }

  #render(): void {
    const filtered = this.#filter === 'all'
      ? this.#people
      : this.#people.filter((p) => effectiveCircles(p, this.#overrides).includes(this.#filter as Circle));

    const counts: Record<Circle, number> = {
      family: 0, friend: 0, work: 0, mentor: 0, school: 0, college: 0, neighbour: 0, occasional: 0,
    };
    for (const p of this.#people) {
      for (const c of effectiveCircles(p, this.#overrides)) counts[c] += 1;
    }

    const tabs = ['all' as const, ...CIRCLES].map((c) => `
      <button class="tab ${this.#filter === c ? 'is-active' : ''}" data-filter="${c}">${escapeHtml(c)}${c === 'all' ? '' : ` <span class="ct">${counts[c as Circle]}</span>`}</button>
    `).join('');

    const rows = filtered.slice(0, 30).map((p) => {
      const active = new Set(effectiveCircles(p, this.#overrides));
      const chips = CIRCLES.map((c) => `
        <button class="chip ${active.has(c) ? 'is-active' : ''}" data-toggle="${escapeHtml(p.id)}|${c}">${escapeHtml(c[0]!.toUpperCase())}</button>
      `).join('');
      return `
<article class="row">
  <div>
    <div class="name">${escapeHtml(p.name)}</div>
    <div class="meta">${RELATIONSHIP_DISPLAY[p.relationship_kind]} · ${escapeHtml(p.city ?? '—')}</div>
  </div>
  <div class="chips">${chips}</div>
</article>`;
    }).join('');

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 14px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--maroon); font-weight: 700; letter-spacing: -0.005em; }
.tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.tab {
  font-family: var(--mono); font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase;
  padding: 7px 12px; border: 1px solid var(--rule); background: var(--paper-up);
  border-radius: 999px; color: var(--ink-dim); cursor: pointer;
}
.tab.is-active { background: var(--maroon); color: var(--paper); border-color: var(--maroon); }
.ct { font-family: var(--mono); font-size: 8px; opacity: 0.85; margin-left: 2px; }
.list { display: grid; gap: 1px; background: var(--rule); border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; }
.row { display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center; background: var(--paper-up); padding: 12px 14px; }
.name { font-family: var(--display); font-size: 16px; color: var(--ink); font-weight: 600; }
.meta { font-family: var(--mono); font-size: 9px; letter-spacing: 0.05em; color: var(--ink-faint); margin-top: 3px; text-transform: lowercase; }
.chips { display: flex; gap: 4px; flex-wrap: wrap; }
.chip {
  width: 24px; height: 24px; border-radius: 50%; border: 1px solid var(--rule);
  background: var(--paper-card); color: var(--ink-faint); font-family: var(--mono);
  font-size: 9px; font-weight: 700; cursor: pointer;
}
.chip.is-active { background: var(--maroon); color: var(--paper); border-color: var(--maroon); }
</style>
<header class="head">
  <span class="eyebrow">Circle tags · multi-belonging</span>
  <h3 class="title">Har dost ek se zyada daayre mein ho sakta hai.</h3>
</header>
<div class="tabs">${tabs}</div>
<div class="list">${rows}</div>
`;

    this.#shadow.querySelectorAll<HTMLButtonElement>('.tab').forEach((b) => {
      b.addEventListener('click', () => {
        const f = b.dataset.filter as Circle | 'all';
        this.#setFilter(f);
      });
    });
    this.#shadow.querySelectorAll<HTMLButtonElement>('.chip').forEach((c) => {
      c.addEventListener('click', () => {
        const [id, circle] = (c.dataset.toggle ?? '').split('|');
        if (id && circle) void this.#toggle(id, circle as Circle);
      });
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('dial-circle-tags', DialCircleTags);
